/**
 * The Rails CSRF token, held in memory and refreshed from every response.
 *
 * PLT-2324 — the legacy frontend sends no token on any XHR, which makes it the
 * largest tokenless write surface in the product and the blocker on PLT-2105's
 * `CSRF_MODE=enforce` flip.
 *
 * WHY THE META TAG ALONE IS NOT ENOUGH, which is the whole reason this module
 * has a holder at all. `csrf_meta_tags` writes ONE mask into the HTML at render
 * time — a snapshot, not a feed. `rotate_session` (app/controllers/concerns/
 * session_rotation.rb) runs on every Devise `sign_in` and on the Spectora Admin
 * masquerade path, and it calls `reset_session`, which discards the raw
 * `session[:_csrf_token]` the rendered mask validates against. From that moment
 * the tag in the DOM is dead, and because the DOM is never re-rendered, reading
 * it per call returns the dead value forever: every write from that tab fails
 * until a full reload. A `CSRF_MODE=log` window measured this directly — 26
 * browser writes arrived with a token present and still failed verification.
 *
 * Rotating on login is CORRECT and must not be "fixed" server-side: the pre-auth
 * token is attacker-plantable, which is exactly what session rotation exists to
 * stop. `rotate_session` does take `keep_csrf: true`, but only two callers pass
 * it (`api/v2/two_factor_controller.rb`), and both are already authenticated —
 * the flag is not available to the login path on purpose.
 *
 * SO THE TOKEN COMES FROM THE RESPONSE, NOT THE DOM. `ApplicationController`
 * emits `X-CSRF-Token` on every response with a non-empty session (PLT-2102's
 * `set_csrf_token_header`), so the value that lands after a rotation is the one
 * minted from the NEW session. Capturing it makes the tab self-heal on its very
 * next response, which is the same mechanism `@spectora/base-api-service` gives
 * the SPAs — and the reason they never had this bug.
 *
 * DEPENDENCY, STATED PLAINLY: `set_csrf_token_header` ships in PLT-2102. This
 * branch is stacked on it for that reason. On a base without it the holder never
 * fills and every read silently falls through to the meta tag — i.e. exactly
 * today's behaviour plus dead code. If this ever lands on a base lacking that
 * `after_action`, this file is not doing what its comment claims.
 *
 * MEMORY ONLY — never a cookie, never `localStorage`. Customer-controlled content
 * is served from `*.hosting.spectora.com`, so anything readable across
 * `.spectora.com` is a disclosure. The value dies with the tab, which is correct:
 * a fresh page load gets a fresh tag and a fresh response.
 */
export const CSRF_HEADER = 'X-CSRF-Token'

// WHY THE HOLDER IS A PAGE-LEVEL SLOT AND NOT A MODULE-LEVEL `let`.
//
// A module-scope variable is one holder per MODULE INSTANCE, and webpack gives
// this module more than one. Checked against the deployed bundles: the id for
// `utils/csrfToken.js` is *defined* in four separate chunks — the shared
// `3872-*`, plus private copies inlined into `client_portal_components-*`,
// `inspection-*` and `spectora_admin-*`. Entries that load a private copy get
// their own closure, so a page running two of them would have two holders.
//
// For the axios path that would still work by luck, since `installCsrfInterceptor`
// is duplicated alongside it and the capture and the read stay co-located. It
// breaks precisely the consumers that have no interceptor of their own — the
// jQuery prefilter in `spectora_admin` and the raw `fetch` calls in
// `packs/reports_widget.js` — which would read an empty holder, fall back to the
// stale tag, and stay poisoned while looking fixed.
//
// One slot on `window` collapses every copy onto the same storage. It is NOT a
// weakening: same-origin script can already read `meta[name="csrf-token"]` and
// issue same-origin writes, so this grants no capability it lacked, and `window`
// is per-origin, so `*.hosting.spectora.com` cannot reach it. Non-enumerable so
// it stays out of `Object.keys(window)` and console noise.
//
// THE ONE HONEST DELTA, since the next reader will ask. After a rotation the slot
// holds a FRESH token while the meta tag holds a dead one, so an attacker who can
// already run script on this origin now finds a valid token where they would
// previously have found an expired one. That is a real difference and it is still
// not a widening: the same attacker can issue a same-origin GET and read the
// `X-CSRF-Token` response header directly, which is where the slot's value came
// from. Script execution on this origin is already game over for CSRF — the
// defense CSRF tokens provide is against a DIFFERENT origin, and that attacker
// can read neither the tag, the header, nor this slot.
const SLOT = '__spectoraCsrfToken'

const slot = () => {
  try {
    if (!Object.prototype.hasOwnProperty.call(window, SLOT)) {
      Object.defineProperty(window, SLOT, {
        value: { token: null },
        writable: true,
        enumerable: false,
        configurable: true,
      })
    }

    return window[SLOT]
  } catch (error) {
    // No `window` (SSR, a bare test harness). Callers degrade to the meta tag,
    // which is exactly today's behaviour.
    return null
  }
}

// The meta tag is the COLD-START path only: the first write of a freshly loaded
// page, before any response has been seen. After that the holder wins, because
// only the holder can be newer than the DOM. (`captureCsrfToken` also writes the
// captured value back into the tag — for jquery_ujs, which reads nothing else —
// so the two agree after the first response; the read order here still stands.)
const readMetaToken = () => {
  try {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // `|| null` rather than `?? null`: an empty-string content attribute is a
    // missing token, not a token whose value is empty.
    return token || null
  } catch (error) {
    // A detached document or a non-browser context (SSR, a test harness without
    // jsdom) must not take a write down with it.
    return null
  }
}

/**
 * Record the token from a response.
 *
 * MUST be called from BOTH the success and the error arm of a response
 * interceptor. Half of this is worse than none: a rotation is very often
 * followed by 4xx/5xx traffic (that is the symptom — the poisoned tab's writes
 * are failing), so capturing only on success means the one tab that needs to
 * heal is the one tab that never does.
 *
 * Header lookup is case-insensitive by hand because the shape differs per
 * client: axios 0.x hands back a plain lower-cased object, `fetch` hands back a
 * `Headers` instance with a `.get()`, and neither is guaranteed here.
 */
export const captureCsrfToken = (headers) => {
  const held = slot()

  try {
    if (!held || !headers) {
      return held?.token ?? null
    }

    const value =
      typeof headers.get === 'function'
        ? headers.get(CSRF_HEADER)
        : headers[CSRF_HEADER] ?? headers[CSRF_HEADER.toLowerCase()]

    // An absent header must NOT clear the holder. Plenty of responses carry no
    // token (an anonymous request, or one whose before_action chain halted, which
    // skips the after_action entirely), and treating those as "the token is gone"
    // would re-poison a tab that had just healed.
    if (value) {
      held.token = value

      // WHY THE TAG IS WRITTEN BACK, when this module exists because the tag
      // cannot be trusted. `jquery_ujs` stamps every sprockets `$.ajax` write
      // (~70 of them, via `application.js` / `static.js`) with `rails.csrfToken()`,
      // which reads `meta[name="csrf-token"]` PER REQUEST and knows nothing about
      // the slot. Refreshing the tag with the value the slot just received lets
      // those writes heal on the same response the slot does; without it a tab
      // poisoned by a rotation elsewhere (an `?al=` link opened in another tab)
      // keeps sending the dead mask on every jquery_ujs write until reload — a
      // 401 per write under `CSRF_MODE=enforce`. Not a weakening: the tag is
      // same-origin-readable already and now merely holds what the slot holds.
      //
      // Refresh only, never create. A page rendered without `csrf_meta_tags` has
      // no jquery_ujs reader to serve, and a tag conjured here would be a second
      // source of truth the next reader has to reason about.
      if (typeof document !== 'undefined') {
        const meta = document.querySelector('meta[name="csrf-token"]')

        if (meta && meta.getAttribute('content') !== value) {
          meta.setAttribute('content', value)
        }
      }
    }
  } catch (error) {
    // Never let telemetry-shaped work break a response — including a detached
    // document or a `querySelector` that is not there (SSR, a bare harness).
  }

  return held?.token ?? null
}

/**
 * The token to send, or `null` when there is none.
 *
 * Read per call, never cached by a caller: the slot is the point of mutability
 * and a caller that snapshots it reintroduces the bug this module exists to fix.
 */
export const getCsrfToken = () => slot()?.token || readMetaToken()

// WHY NOTHING PRIMES THE SLOT FROM THE TAG AT BOOT, since it looks like an
// obvious cold-start improvement and was written once before being removed.
//
// It buys nothing. Every reader already falls back to the tag on its own when
// the slot is empty: `getCsrfToken` above, and the sprockets helper
// (`app/assets/javascripts/shared/csrf_token.js`), whose `spectoraCsrfToken()`
// is literally `readSlot() || readMeta()`.
//
// And it costs something real. An empty slot means every read hits the LIVE tag,
// so a tag the page replaces after load is picked up immediately. Priming turns
// that into a render-time snapshot held for the life of the tab, because the read
// order prefers the slot: on Spectora Admin, Turbolinks 5 swaps the head's meta
// tag per visit and a masquerade / `?al=` navigation runs `rotate_session`, so a
// primed slot would keep handing out a dead mask on every sprockets and jQuery
// write. `installTurbolinksCsrfCapture` heals the slot on an admin visit, but
// only there — everywhere else an empty slot is the fresher answer.
//
// The slot is therefore filled by `captureCsrfToken` and nothing else: it holds a
// value taken from a response, which is the only thing that can be newer than the
// DOM, and that is exactly what makes `getCsrfToken`'s slot-first read correct.

/**
 * PLT-2867 — fetch a token for the session as it stands NOW.
 *
 * WHY A GET, AND NOT JUST WAITING FOR THE NEXT RESPONSE. A mobile browser drops
 * `_spectora_session` and later restores the tab from cache, still holding the token
 * of the session that is gone. `remember_user_token` recreates a session on the next
 * request, with a new token, but if that request is a WRITE it is refused first:
 * under `:null_session` the refused write runs with a `NullCookieJar`, so nothing is
 * recreated and the 401 carries no token. Only a request that runs the full cookie
 * jar heals the tab, and `GET /csrf_token` (`CsrfTokensController`) is the cheapest
 * one: 204, `no-store`, no redirect, no side effect.
 *
 * WHAT IT REFRESHES. The response goes through `captureCsrfToken`, which updates the
 * slot (axios, the sprockets `spectoraCsrfToken()` helper) and the meta tag
 * (`jquery_ujs`). It also rewrites the `authenticity_token` inputs of same-origin
 * forms, which is what `rails-ujs`'s `refreshCSRFTokens` does, so a login form
 * restored from cache posts a live token. The global token validates any form.
 *
 * It resolves to `{ token, authenticated }`: the token from the response, or `null`,
 * and `true` / `false` from `X-Spectora-Authenticated`, or `null` when the header is
 * absent (a server without the route). It never rejects, and concurrent calls share
 * one request.
 */
export const REFRESH_PATH = '/csrf_token'
export const AUTHENTICATED_HEADER = 'X-Spectora-Authenticated'

const UNKNOWN_REFRESH = Object.freeze({ token: null, authenticated: null })

const readHeader = (headers, name) => {
  try {
    return headers?.get?.(name) || null
  } catch (error) {
    return null
  }
}

const isSameOriginAction = (form) => {
  try {
    // `getAttribute`, never `form.action`: a form with an input named `action`
    // shadows the property with that element.
    const action = form.getAttribute('action') || window.location.href

    return (
      new URL(action, window.location.href).origin === window.location.origin
    )
  } catch (error) {
    return false
  }
}

// Refresh only, never create: a form rendered without a token is not ours to arm.
const rewriteFormTokens = (token) => {
  try {
    document
      .querySelectorAll('form input[name="authenticity_token"]')
      .forEach((input) => {
        if (input.form && isSameOriginAction(input.form)) {
          input.value = token
        }
      })
  } catch (error) {
    // A detached document or a non-browser context must not fail the refresh.
  }
}

const requestRefresh = async () => {
  try {
    if (typeof fetch !== 'function') {
      return UNKNOWN_REFRESH
    }

    const response = await fetch(REFRESH_PATH, {
      credentials: 'same-origin',
      cache: 'no-store',
    })
    const token = readHeader(response.headers, CSRF_HEADER)
    const flag = readHeader(response.headers, AUTHENTICATED_HEADER)

    // An absent header leaves the slot alone, exactly as it does on any response.
    captureCsrfToken(response.headers)
    if (token) {
      rewriteFormTokens(token)
    }

    return {
      token,
      authenticated: flag === '1' ? true : flag === '0' ? false : null,
    }
  } catch (error) {
    // Offline, aborted, or blocked: the page keeps the token it has.
    return UNKNOWN_REFRESH
  }
}

export const refreshCsrfToken = () => {
  const held = slot()

  // The pending request lives on the page-level slot, not in this module, so every
  // copy of this module webpack emits shares it (see the SLOT comment above).
  if (held?.refreshing) {
    return held.refreshing
  }

  const pending = requestRefresh()

  if (held) {
    held.refreshing = pending
    pending.then(() => {
      if (held.refreshing === pending) {
        held.refreshing = null
      }
    })
  }

  return pending
}

/**
 * Claim the one-per-page installation of the lifecycle refresh. Returns `true` to
 * the first caller and `false` after that, or when there is no `window`. The flag
 * lives on the slot for the same reason the token does: the bootstrap runs once per
 * entry chunk, and each chunk has its own copy of the installing module.
 */
export const claimCsrfLifecycle = () => {
  const held = slot()

  if (!held || held.lifecycleInstalled) {
    return false
  }

  held.lifecycleInstalled = true

  return true
}

// Test seam. The slot outlives a module instance, so a spec that captures a token
// would otherwise leak it into the next example.
export const resetCsrfToken = () => {
  const held = slot()
  if (held) {
    held.token = null
    held.refreshing = null
  }
}

export default getCsrfToken
